import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { MoneyInput } from "../../../components/form-components/MoneyInput";
import { simulatePreview } from "../policyBuilderState";

const PolicyPreviewSimulator = ({ tiers, objectType, currency = "EUR" }) => {
  const [amountDelta, setAmountDelta] = useState(null);
  const [nearTerm, setNearTerm] = useState(false);
  const [withinDays, setWithinDays] = useState(90);

  const result = useMemo(
    () => simulatePreview({ amountDelta, nearTerm, withinDays }, tiers),
    [amountDelta, nearTerm, withinDays, tiers]
  );

  return (
    <div className="bg-card border border-card-bd rounded-card p-4 xl:sticky xl:top-4 space-y-4">
      <h3 className="text-sm font-semibold text-text">Preview & Simulate</h3>

      <div>
        <label className="block text-xs font-medium text-text-2 mb-1">Amount delta</label>
        <MoneyInput
          value={amountDelta}
          onChange={setAmountDelta}
          currency={currency}
          placeholder="Enter amount..."
          allowNegative={true}
          maxWidth="100%"
          autoFocus
        />
      </div>

      {objectType === "budget_period_version" && (
        <div className="space-y-2">
          <label className="flex items-center gap-1.5 text-xs text-text-2 cursor-pointer">
            <input
              type="checkbox"
              checked={nearTerm}
              onChange={(e) => setNearTerm(e.target.checked)}
            />
            Near-term
          </label>
          {nearTerm && (
            <div>
              <label className="block text-xs font-medium text-text-2 mb-1">Within days</label>
              <input
                type="number"
                className="w-full p-2 border border-border rounded-ctrl text-sm"
                value={withinDays}
                onChange={(e) => setWithinDays(parseInt(e.target.value, 10) || 0)}
              />
            </div>
          )}
        </div>
      )}

      {/* Result */}
      <div className="pt-3 border-t border-border space-y-2">
        <div className={`p-3 rounded-card text-sm ${
          result.approvalRequired
            ? "bg-warning-soft border border-warning"
            : result.matchedTier
              ? "bg-success-soft border border-success"
              : "bg-surface-2 border border-border"
        }`}>
          <div className="font-medium text-text mb-1">
            {result.matchedTier
              ? `Matched: ${result.matchedTier}`
              : "No tier matched"
            }
          </div>
          <div className="text-xs text-text-2">
            {result.approvalRequired ? (
              <>
                <div className="font-medium text-warning mb-1">Approval required</div>
                <div>{result.chainSummary}</div>
              </>
            ) : (
              <div className="text-success">No approval required</div>
            )}
          </div>
        </div>

        {/* Condition explanation */}
        {result.matchedConditions && (
          <div className="text-xs text-text-2 space-y-0.5 px-1">
            <div>Amount in range: {result.matchedConditions.amountInRange ? "yes" : "no"}</div>
            <div>Context match: {result.matchedConditions.contextMatch ? "yes" : "n/a"}</div>
          </div>
        )}

        {/* Enforced roles detail */}
        {result.enforcedRoles.length > 0 && (
          <div className="text-xs px-1">
            <div className="font-medium text-text-2 mb-1">Role enforcement:</div>
            <div className="space-y-0.5">
              {result.enforcedRoles.map((r) => (
                <div key={r.role} className="flex items-center gap-1 text-text-2">
                  <span>{r.label}:</span>
                  {r.requiredByTier && <span className="text-warning">required</span>}
                  {r.inChainAtStep !== null && (
                    <span className="text-success">in chain (step {r.inChainAtStep + 1})</span>
                  )}
                  {r.requiredByTier && r.inChainAtStep === null && (
                    <span className="text-danger">missing from chain</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

PolicyPreviewSimulator.propTypes = {
  tiers: PropTypes.array.isRequired,
  objectType: PropTypes.string,
  currency: PropTypes.string,
};

export default PolicyPreviewSimulator;
